import { memo } from 'react';
import { Avatar } from '@cyphex/ui';
import type { TypingEntry } from '../../types';

interface TypingRowProps {
  entries: readonly TypingEntry[];
}

function summarise(entries: readonly TypingEntry[]): string {
  const names = entries.map((entry) => entry.displayName);
  if (names.length === 1) return `${names[0]} is typing`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing`;
  return `${names[0]} and ${names.length - 1} others are typing`;
}

/** Live typing indicator. Rendered outside the virtualiser so it never shifts rows. */
export const TypingRow = memo(function TypingRow({ entries }: TypingRowProps) {
  if (entries.length === 0) return null;
  const first = entries[0];

  return (
    <div
      className="flex items-center gap-2.5 px-4 py-1.5"
      aria-live="polite"
      aria-atomic="true"
    >
      {first ? <Avatar name={first.displayName} size="sm" /> : null}
      <span className="flex items-center gap-1 rounded-pill bg-subtle px-2.5 py-1.5">
        {[0, 1, 2].map((index) => (
          <span
            key={index}
            style={{ animationDelay: `${index * 140}ms` }}
            className="h-1.5 w-1.5 animate-typing-dot rounded-pill bg-muted-foreground"
          />
        ))}
      </span>
      <span className="text-sm text-muted-foreground">{summarise(entries)}</span>
    </div>
  );
});
